/* Deriving a family's GUARDIANS for the customer Family page.
 *
 * WHY THIS IS DERIVED FROM STUDENTS RATHER THAN READ DIRECTLY. A customer is
 * permitted exactly one family-scoped read by `firestore.rules`:
 * `students where familyId == myFamilyId()`. The two places guardian identity
 * would more naturally live are both closed to them —
 *   families/{id}     read: isAdmin() || isOwner()        (no customer path)
 *   users/{uid}       read: request.auth.uid == userId    (SELF only)
 * — so a parent cannot read their own family doc, and cannot read a second
 * guardian's user doc even within their own family. Surfacing guardians from
 * `students.parent1` / `students.parent2` needs NO rules change and no new
 * collection, and it is the same data the studio actually operates on: the
 * family-binding migration grouped households by `parent1.email`.
 *
 * IF A REAL family-member ENTITY EVER LANDS, THIS BECOMES AN ADAPTER, NOT A
 * FOUNDATION. Do not build management (invite a second guardian, edit a
 * guardian) on top of these fields — a write here would be a write onto every
 * student doc that happens to repeat the same parent, which is exactly the
 * denormalization the Songs build refused. Guardian invites already exist
 * server-side as `createGuardianInvite`.
 *
 * SHAPES OBSERVED IN PRODUCTION (2026-08-10, read before writing this):
 *   - `parent1` is populated on every real student; `parent2` is `null` on most
 *     but genuinely populated on some (McGowan: Raina Young + George McGowan;
 *     Hesse: Dani + Troy). It is NOT a dead field.
 *   - Test/demo students carry a `parent2` whose every string is empty — an
 *     all-blank object must read as ABSENT, or those families grow a nameless
 *     ghost guardian.
 *   - A guardian's surname frequently differs from the student's (Gabby Frye →
 *     Rebecca Charles; Natalie Erdmann → Karen Nelson). Never infer one from
 *     the other, and never render "the Frye family".
 *   - Guardians on the same student can have DIFFERENT addresses (McGowan:
 *     Pinetree Pass vs River Valley Lane — separated parents). There is no
 *     single family address, which is why none is shown.
 *   - Phone formatting is inconsistent across the MMS import: '6126708252',
 *     '651-815-1367', '651 341-9753', '16124083058'.
 */

const blank = (v) => !String(v ?? '').trim();

/** A parent slot counts as present only if it carries a name or an email. */
function isRealGuardian(p) {
	if (!p || typeof p !== 'object') return false;
	return !(blank(p.firstName) && blank(p.lastName) && blank(p.email));
}

export function guardianName(p) {
	const name = [p.firstName, p.lastName]
		.map((s) => String(s ?? '').trim())
		.filter(Boolean)
		.join(' ');
	return name || String(p.email ?? '').trim() || 'Guardian';
}

/* Display a US phone as (952) 555-0123 when it plainly is one, and otherwise
   leave it exactly as stored. Reformatting something we do not understand is
   how a number becomes unusable — the stored string is the one the studio
   dials. Handles the leading-1 form seen in the MMS import. */
export function formatPhone(raw) {
	const s = String(raw ?? '').trim();
	if (!s) return '';
	const digits = s.replace(/\D/g, '');
	const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
	if (ten.length !== 10) return s;
	return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/* Collapse every parent slot across the family's students into one guardian
 * list.
 *
 * DEDUPE BY EMAIL FIRST, because that is the key the rest of the system already
 * treats as identity (familyResolution, the binding migration). Name is only a
 * fallback for a guardian with no address on file — two different people
 * sharing a name inside one family is far less likely than one person appearing
 * on three children.
 *
 * The signed-in parent is marked rather than filtered out: a Family page that
 * silently omits the person reading it is the bug the old copy already had
 * ("Everyone linked to your account" listing only children).
 */
export function deriveGuardians(students, viewerEmail) {
	const viewer = String(viewerEmail ?? '').trim().toLowerCase();
	const byKey = new Map();

	for (const student of students || []) {
		for (const slot of [student?.parent1, student?.parent2]) {
			if (!isRealGuardian(slot)) continue;
			const email = String(slot.email ?? '').trim();
			const key = email ? `e:${email.toLowerCase()}` : `n:${guardianName(slot).toLowerCase()}`;

			const existing = byKey.get(key);
			if (existing) {
				// Same person on a second child: keep the fullest record rather
				// than the first one seen — a phone recorded on one sibling and
				// not the other should still show.
				if (!existing.phone && slot.phone) existing.phone = String(slot.phone).trim();
				if (!existing.email && email) existing.email = email;
				existing.studentIds.push(student.id);
				continue;
			}

			byKey.set(key, {
				key,
				name: guardianName(slot),
				email,
				phone: String(slot.phone ?? '').trim(),
				isViewer: !!viewer && email.toLowerCase() === viewer,
				studentIds: [student.id],
			});
		}
	}

	// The person reading the page comes first; everyone else keeps discovery
	// order, which follows the student order the caller already chose.
	const all = [...byKey.values()];
	return [...all.filter((g) => g.isViewer), ...all.filter((g) => !g.isViewer)];
}
